import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Button, Card, Checkbox, Empty, List, message, Modal, Radio, Space, Spin, Tag, Typography } from "antd";
import { MailOutlined } from "@ant-design/icons";
import axios from "axios";
import type { Device } from "./types";
import {
  buildNotificationRecipients,
  loadBbsNotificationSettings,
  normalizeEmail,
  recipientLabel,
  snapshotEmailSettings,
  WEB_REQUEST_ACTIONS,
  WEB_REQUEST_COMMAND,
  type NotificationRecipient,
  type RecipientMode,
} from "./NotificationSettingsShared";

const ALL_LABEL = "管理者と親族・知人全員";

export type NotificationEditorHandle = {
  open: () => void;
};

interface Props {
  selectedDevice: Device | null;
  className?: string;
  style?: React.CSSProperties;
  hideTrigger?: boolean;
}

const BbsNotificationEditor = forwardRef<NotificationEditorHandle, Props>(({ selectedDevice, className, style, hideTrigger }, ref) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([]);
  const [mode, setMode] = useState<RecipientMode>("all");
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [loadedSnapshot, setLoadedSnapshot] = useState("");
  const loadSeqRef = useRef(0);
  const openedDeviceIdRef = useRef<string | null>(null);

  const isDirty = snapshotEmailSettings(mode, selectedEmails) !== loadedSnapshot;

  const loadEditorData = useCallback(async () => {
    if (!selectedDevice) {
      setRecipients([]);
      return;
    }
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const settings = await loadBbsNotificationSettings(selectedDevice.id);
      if (seq !== loadSeqRef.current) return;

      const nextRecipients = buildNotificationRecipients(
        selectedDevice.managerEmail,
        selectedDevice.familyEmails
      );
      const allowed = new Set(nextRecipients.map((x) => x.email));
      const nextSelected = settings.selectedEmails.filter((email) => allowed.has(email));
      setRecipients(nextRecipients);
      setMode(settings.mode);
      setSelectedEmails(nextSelected);
      setLoadedSnapshot(snapshotEmailSettings(settings.mode, nextSelected));
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      console.error("掲示板通知設定の取得に失敗:", e);
      message.error("掲示板通知設定の取得に失敗しました。");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [selectedDevice]);

  const closeEditor = () => {
    loadSeqRef.current += 1;
    setEditorOpen(false);
    setRecipients([]);
    setMode("all");
    setSelectedEmails([]);
    setLoadedSnapshot("");
    setLoading(false);
  };

  const handleOpenEditor = () => {
    if (!selectedDevice) {
      message.warning("デバイスを選択してください。");
      return;
    }
    openedDeviceIdRef.current = selectedDevice.id;
    setRecipients([]);
    setMode("all");
    setSelectedEmails([]);
    setLoadedSnapshot("");
    setEditorOpen(true);
    void loadEditorData();
  };

  useImperativeHandle(ref, () => ({ open: handleOpenEditor }), [selectedDevice]);

  useEffect(() => {
    if (!editorOpen) return;
    if (selectedDevice?.id === openedDeviceIdRef.current) return;
    closeEditor();
  }, [editorOpen, selectedDevice?.id]);

  const handleCloseEditor = async () => {
    if (isDirty) {
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: "未送信の変更があります",
          content: "タブレットへ未送信の編集内容があります。破棄して閉じますか？",
          okText: "破棄して閉じる",
          cancelText: "キャンセル",
          okButtonProps: { danger: true },
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
    }
    closeEditor();
  };

  const toggleEmail = (email: string, checked: boolean) => {
    setSelectedEmails((prev) =>
      checked ? (prev.includes(email) ? prev : [...prev, email]) : prev.filter((x) => x !== email)
    );
  };

  const handleSendToTablet = async () => {
    if (!selectedDevice?.fcmToken) {
      message.warning("FCMトークンが未設定のため、タブレットへ送信できません。");
      return;
    }
    if (mode === "custom" && selectedEmails.length === 0) {
      message.warning("個別送信を選ぶ場合は送信先を1件以上選択してください。");
      return;
    }
    const payload = {
      mode,
      selectedEmails: selectedEmails.map(normalizeEmail).filter(Boolean),
    };
    setSending(true);
    try {
      const requestId = crypto.randomUUID();
      await axios.post("/api/sendFCM", {
        tokens: [selectedDevice.fcmToken],
        payload: {
          mode: "request",
          command: WEB_REQUEST_COMMAND,
          param1: WEB_REQUEST_ACTIONS.bbs,
          param2: JSON.stringify(payload),
          param3: requestId,
          param4: String(Date.now()),
        },
      });
      message.success("タブレットに送信しました");
      closeEditor();
    } catch (e) {
      console.error("FCM送信エラー:", e);
      message.error("タブレットへの送信に失敗しました。");
    } finally {
      setSending(false);
    }
  };

  const summaryText = mode === "custom" ? `現在の選択: ${selectedEmails.length}件` : `現在の選択: ${ALL_LABEL}`;

  return (
    <>
      {!hideTrigger && (
      <div className={className} style={{ display: "inline-block", ...style }}>
        <Button type="primary" onClick={handleOpenEditor} disabled={!selectedDevice}>
          掲示板通知設定
        </Button>
      </div>
      )}

      <Modal
        title={
          <span>
            <MailOutlined style={{ marginRight: 8, color: "#4f6ef7" }} />
            掲示板通知設定 - {selectedDevice?.deviceName || selectedDevice?.serialNumber}
          </span>
        }
        open={editorOpen}
        onCancel={() => void handleCloseEditor()}
        width={720}
        centered
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => void handleCloseEditor()}>
            閉じる
          </Button>,
          <Button key="send" type="primary" loading={sending} onClick={() => void handleSendToTablet()} disabled={!selectedDevice || loading}>
            タブレットへ送信して反映
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          掲示板に投稿があったときの通知先を設定します。送信しないで閉じると、変更は破棄されます。
        </Typography.Paragraph>

        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : recipients.length === 0 ? (
          <Empty description="送信先候補がありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            <Typography.Text strong>掲示板通知の送信モード</Typography.Text>
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value as RecipientMode)}
              style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8, marginBottom: 16 }}
            >
              <Radio value="all">{ALL_LABEL}に一斉送信（推奨）</Radio>
              <Radio value="custom">選択した送信先のみに送信</Radio>
            </Radio.Group>

            <Typography.Text strong>通知先メール</Typography.Text>
            <div style={{ marginTop: 4, marginBottom: 8 }}>{summaryText}</div>
            <List
              dataSource={recipients}
              renderItem={(item) => (
                <List.Item>
                  <Checkbox
                    checked={mode === "all" || selectedEmails.includes(item.email)}
                    disabled={mode !== "custom"}
                    onChange={(e) => toggleEmail(item.email, e.target.checked)}
                  >
                    <Space wrap>
                      <Tag color={item.isManager ? "gold" : "blue"}>{recipientLabel(item)}</Tag>
                      <Typography.Text>{item.email}</Typography.Text>
                    </Space>
                  </Checkbox>
                </List.Item>
              )}
            />
          </>
        )}
      </Modal>
    </>
  );
});

BbsNotificationEditor.displayName = "BbsNotificationEditor";

export default BbsNotificationEditor;
